/* eslint-disable no-restricted-syntax */
import _ from 'lodash-es';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { ITerminalData, ITerminalItem, ISubTerminalItem } from '../../types/Locations.type';
import { INominationHistory, INominationHistoryEntryExit } from '../../types/Nomination.type';
import { sendNotification } from '../../UI/Notification';
import { UK_TIME_ZONE, NUMBERS } from '../../constants/Common';
import { ISelectOptions } from '../../types/Common.type';
import { GenesisApi, isRequestCanceled } from '../../services/GenesisApi';
import { ITerminalMapping } from '../../types/FieldNominations.type';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const ERROR_OCCURRED = 'An error occurred';
const BAD_REQUEST = 'Bad Request';
const INTERNAL_SERVER_ERROR = 'Internal Server Error';

export const INTERVAL_TIME_120000 = 120000;

export interface IPaths {
  key: string;
  label: string;
  path?: string;
  children?: IPaths[];
}

export function getCurrentGasDay() {
  let currentGasDay = dayjs().tz(UK_TIME_ZONE);
  if (currentGasDay.hour() < NUMBERS.FIVE) {
    currentGasDay = currentGasDay.subtract(1, 'day');
  }
  return currentGasDay;
}

// dedupes options by id, keeping the last occurrence
export const uniqueFieldArray = (fields: ISelectOptions[]) => [
  ...new Map(fields.map((item) => [item.id, item])).values(),
];

const filterFieldsByDate = (fields: ISelectOptions[], date: string) => {
  return fields.filter((field) => {
    if (!field.validFrom && !field.validTo) {
      return true; // unmapped source prod field
    }
    const fieldFromDate = dayjs(field.validFrom).startOf('day');
    const fieldToDate = dayjs(field.validTo).startOf('day');
    return (
      dayjs(date).isSame(fieldFromDate, 'day') ||
      dayjs(date).isSame(fieldToDate, 'day') ||
      dayjs(date).isBetween(fieldFromDate, fieldToDate, null, '[]')
    );
  });
};

export const filterFieldsByDateRange = (
  fields: ISelectOptions[],
  filterType: string,
  currentDate: string,
  nextDate: string,
  fromDate: string,
  toDate: string,
) => {
  if (filterType === 'WD') {
    return filterFieldsByDate(fields, currentDate);
  }
  if (filterType === 'DA') {
    return filterFieldsByDate(fields, nextDate);
  }
  return fields.filter((field: ISelectOptions) => {
    if (!field.validFrom && !field.validTo) {
      return true; // unmapped source prod field
    }
    const fieldFromDate = dayjs(field.validFrom).startOf('day');
    const fieldToDate = dayjs(field.validTo).startOf('day');
    const selectedFromDate = dayjs(fromDate).startOf('day');
    const selectedToDate = dayjs(toDate).startOf('day');

    return (
      (selectedFromDate.isSameOrAfter(fieldFromDate, 'day') &&
        selectedFromDate.isSameOrBefore(fieldToDate, 'day')) ||
      (selectedToDate.isSameOrAfter(fieldFromDate, 'day') &&
        selectedToDate.isSameOrBefore(fieldToDate, 'day'))
    );
  });
};

export function getIsPastGasDay(gasDay: string) {
  const currentGasDay = getCurrentGasDay();
  return dayjs.tz(gasDay, UK_TIME_ZONE).isBefore(currentGasDay, 'day');
}

export function getIsWithinDay(gasDay: string) {
  const currentGasDay = getCurrentGasDay();
  return dayjs.tz(gasDay, UK_TIME_ZONE).isSame(currentGasDay, 'day');
}

export function getIsDayAhead(gasDay: string) {
  const currentGasDay = getCurrentGasDay();
  return dayjs.tz(gasDay, UK_TIME_ZONE).isSame(currentGasDay.add(1, 'days'), 'day');
}

export function getAllPaths(array: IPaths[]) {
  const paths: string[] = [];

  function collectPaths(arr: IPaths[]) {
    for (const obj of arr) {
      if (obj.path) {
        paths.push(obj.path);
      }
      if (obj.children) {
        collectPaths(obj.children);
      }
    }
  }
  collectPaths(array);
  return paths;
}

export function extractFirstPaths(path: string): string {
  const nextSlashIndex = path.indexOf('/', 1);
  if (nextSlashIndex === -1) {
    return path;
  }
  return path.substring(0, nextSlashIndex);
}

export function findKeyByPath(array: IPaths[], path: string): string | undefined {
  for (const obj of array) {
    if (obj.path?.includes(path)) {
      return obj.key;
    }
    if (obj.children) {
      const key = findKeyByPath(obj.children, path);
      if (key) {
        return key;
      }
    }
  }
  return undefined;
}

export function searchByText(data: unknown, searchText: string, properties) {
  // Helper function to check if any property contains the search text
  const containsText = (item, text, props) => {
    if (_.isEmpty(text)) {
      return true;
    } // If text is empty, consider all items as matching
    const lowerText = _.toLower(text);
    return _.some(props, (prop) => {
      return _.includes(_.toLower(item[prop] || ''), lowerText);
    });
  };

  // Recursive function to filter the nested data
  const filterNestedData = (items: unknown, text: string, props) => {
    return _.compact(
      _.map(items, (item) => {
        const matches = containsText(item, text, props);

        // Recursively filter subRows if they exist
        const subRows = item.subRows ? filterNestedData(item.subRows, text, props) : [];

        // Return the item if it matches or if it has matching subRows
        if (matches || !_.isEmpty(subRows)) {
          return { ...item, subRows };
        }

        return null;
      }),
    );
  };

  return filterNestedData(data, searchText, properties);
}

export function transformData(response: ITerminalItem[] | ISubTerminalItem[]) {
  const result: ITerminalData[] = [];
  const terminalMap = new Map();

  response?.forEach((item) => {
    const {
      terminalId,
      terminalName,
      subTerminalId,
      subTerminalName,
      meterId,
      terminalIsActive,
      subTerminalIsActive,
      endurLocation,
      etrmExternalCode,
      isInterconnectionPoint,
      eicCode,
      adjacentTsoId,
      adjacentTsoName,
      geminiLocationCode,
      cvaCode,
      deal_DealLegId,
    } = item;

    // Ensure terminal entry in the map
    if (!terminalMap.has(terminalId)) {
      terminalMap.set(terminalId, {
        id: terminalId.toString(),
        terminalName,
        subTerminalName: '',
        meterId: '',
        cvaCode: '',
        status: terminalIsActive ? 'Active' : 'Inactive',
        isActive: terminalIsActive,
        isInterconnectionPoint: null,
        geminiLocationCode,
        add: true,
        subRows: [],
        parentName: terminalName,
      });
    }

    const terminalEntry = terminalMap.get(terminalId);

    // Add sub-terminal to the correct terminal entry
    if (subTerminalId && subTerminalName !== null) {
      const subTerminalEntry = {
        id: `${terminalId}-${subTerminalId}`,
        terminalName: '',
        subTerminalName,
        meterId,
        cvaCode,
        status: subTerminalIsActive ? 'Active' : 'Inactive',
        isActive: subTerminalIsActive,
        add: false,
        parentName: terminalName,
        endurLocation,
        etrmExternalCode,
        isInterconnectionPoint,
        eicCode,
        adjacentTsoId,
        adjacentTsoName,
        deal_DealLegId,
      };

      // Insert the subTerminalEntry in sorted order by subTerminalName
      const index = terminalEntry.subRows.findIndex(
        (subRow) => subRow.subTerminalName.toLowerCase() > subTerminalName.toLowerCase(),
      );
      if (index === -1) {
        terminalEntry.subRows.push(subTerminalEntry);
      } else {
        terminalEntry.subRows.splice(index, 0, subTerminalEntry);
      }
    }
  });

  // Convert map values to result array
  terminalMap?.forEach((value) => result.push(value));

  return result;
}

export const transformResponse = (noms: INominationHistory[]) => {
  return noms.map((item) => {
    // Destructure the item and prepare the top-level entry
    const { description, nominationSourceType, history } = item;

    // Create the top-level output item
    const mainItem = {
      id: history[0].id,
      description,
      nominationSourceType,
      start: `${history[0].start}:00`,
      nomination: history[0].nomination,
      change: history[0].change,
      changePercentage:
        Math.abs(parseFloat(String(history[0].changePercentage))) >= 0.01
          ? parseFloat(String(history[0].changePercentage)).toFixed(2)
          : 0,
      source: history[0].source,
      createdTimestamp: history[0].createdTimestamp,
      subRows: [] as INominationHistoryEntryExit[],
    };

    history.slice(1).forEach((entry) => {
      // Create the subRow entry
      const subRow: INominationHistoryEntryExit = {
        id: entry.id,
        description,
        nominationSourceType,
        start: `${entry.start}:00`,
        nomination: entry.nomination,
        change: entry.change,
        changePercentage:
          Math.abs(parseFloat(String(entry.changePercentage))) >= 0.01
            ? parseFloat(String(entry.changePercentage)).toFixed(2)
            : 0,
        source: entry.source,
        createdTimestamp: entry.createdTimestamp,
      };
      // Push subRow to the subRows array
      mainItem.subRows.push(subRow);
    });
    // Return the final output item
    return mainItem;
  });
};

export const formatToThousandSeparator = (value: number | Decimal) => {
  const parts = value.toString().split('.');
  const integerPart = parseInt(parts[0], 10).toLocaleString('en-US');
  const decimalPart = parts[1] ? `.${parts[1]}` : '';
  return integerPart + decimalPart;
};

export const renderRefreshMessage = (value: number | null) => {
  // empty message at initial stage
  if (value === null) {
    return null;
  }
  if (value === 0) {
    return 'Last refreshed less than 10 seconds ago';
  }
  return `Last refreshed ${value} minute${value > 1 ? 's' : ''} ago`;
};

export const sortByManualDeals = (tradingData) => {
  return tradingData.sort((a, b) => {
    if (a.dealType === 'Manual' && b.dealType !== 'Manual') {
      return -1;
    }
    if (a.dealType !== 'Manual' && b.dealType === 'Manual') {
      return 1;
    }
    return 0;
  });
};

export const formatTimeUnit = (value: number, unit: string) => {
  return `${value} ${unit}${value > 1 ? 's' : ''} ago`;
};

const DAYS = 30;
const MONTHS = 12;

export const formatDuration = (ms: number) => {
  if (ms < 1000) {
    return 'less than a second ago';
  }
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) {
    return formatTimeUnit(seconds, 'second');
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return formatTimeUnit(minutes, 'minute');
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return formatTimeUnit(hours, 'hour');
  }
  const days = Math.floor(hours / 24);
  if (days < DAYS) {
    return formatTimeUnit(days, 'day');
  }
  const months = Math.floor(days / DAYS);
  if (months < MONTHS) {
    return formatTimeUnit(months, 'month');
  }
  const years = Math.floor(months / MONTHS);
  return formatTimeUnit(years, 'year');
};

export const compareValues = <T>(valA: T, valB: T, direction: 'asc' | 'desc'): number => {
  const isEmpty = (v: unknown) => v === null || v === undefined || v === '';

  const aEmpty = isEmpty(valA);
  const bEmpty = isEmpty(valB);

  // Empty values: top in asc, bottom in desc
  if (aEmpty || bEmpty) {
    if (aEmpty && bEmpty) {
      return 0;
    }
    return direction === 'asc' ? (aEmpty ? -1 : 1) : aEmpty ? 1 : -1;
  }

  // Strings
  if (typeof valA === 'string' && typeof valB === 'string') {
    const cmp = valA.localeCompare(valB);
    return direction === 'asc' ? cmp : -cmp;
  }

  // Numbers / dates / comparable primitives
  if (valA === valB) {
    return 0;
  }

  if (direction === 'asc') {
    return valA < valB ? -1 : 1;
  }
  return valA > valB ? -1 : 1;
};

export const handleError = (error, customMessage) => {
  if (isRequestCanceled(error)) {
    return;
  }

  if (
    !error?.message?.includes(ERROR_OCCURRED) &&
    !error?.message?.includes(BAD_REQUEST) &&
    !error?.message?.includes(INTERNAL_SERVER_ERROR)
  ) {
    sendNotification({
      type: 'error',
      message: typeof error?.response?.data === 'string' ? error?.response?.data : customMessage,
    });
  }
};

export const checkExisting = (
  keys: string[],
  existingData: ITerminalMapping[],
  formData: ITerminalMapping,
): boolean => {
  return existingData.some((record) => keys.every((key) => record[key] === formData[key]));
};

export const isFieldActive = (validFrom: string, validTo: string) => {
  const currentGasDay = getCurrentGasDay().startOf('day');

  const validFromDayjs = dayjs(validFrom).startOf('day');
  const validToDayjs = dayjs(validTo).startOf('day');
  return (
    currentGasDay.isSameOrAfter(validFromDayjs, 'day') &&
    currentGasDay.isSameOrBefore(validToDayjs, 'day')
  );
};

export const handleDownload = async (
  url: string,
  filename: string,
  method?: string,
  payload?: Record<string, unknown>,
) => {
  const response = await GenesisApi({
    url,
    method: method ?? 'POST',
    responseType: 'blob',
    ...(payload && { data: payload }),
  });
  const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(url);
  link.remove();
};

export const isNullOrEmptyObject = (obj) => {
  return obj === null || (typeof obj === 'object' && Object.keys(obj).length === 0);
};

export const renderEnergy = (energy: string, value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return '-';
  }
  switch (energy) {
    case 'GWh':
      // KWh to GWh
      if (value === 0) {
        return 0;
      }
      return (value / NUMBERS.MILLION).toLocaleString('en-US', {
        minimumFractionDigits: NUMBERS.FIVE,
      });
    case 'KWh':
      // KWh to KWh
      if (value === 0) {
        return 0;
      }
      return value.toLocaleString('en-US');
    case 'Therms':
      // KWh to Therms
      if (value === 0) {
        return 0;
      }
      return Math.round(value / NUMBERS.THERMS_KWH_CONVERSION).toLocaleString('en-US');
    default:
      // KWh to TJ
      if (value === 0) {
        return 0;
      }
      return (value * NUMBERS.TJ_CONVERSION).toLocaleString('en-US', {
        minimumFractionDigits: NUMBERS.FIVE,
      });
  }
};

const nullAwareSum = (values: (number | null | undefined)[]) => {
  const realValues = values.filter(
    (v) => v !== null && v !== undefined && v !== Infinity && !Number.isNaN(v),
  ) as number[];

  if (realValues.length === 0) {
    return Infinity; // all null/missing → show blank
  }
  return realValues.reduce((acc, v) => acc + v, 0);
};

export const addTotalsToTransformedRes = (data) => {
  return data.map((cp) => {
    const processedGroups = (cp.groups ?? []).map((group) => {
      const contracts = group.contracts ?? [];

      // 1) Collect all day keys
      const allDayKeys = new Set<string>();
      contracts.forEach((contract) => {
        Object.keys(contract.dayWiseAllocation ?? {}).forEach((day) => allDayKeys.add(day));
      });

      const days = Array.from(allDayKeys);

      // 2) Per-contract totals (null-aware)
      const processedContracts = contracts.map((contract) => {
        const dayWise = contract.dayWiseAllocation ?? {};
        const contractValues = Object.values(dayWise).map((v) =>
          v === Infinity || v === null || v === undefined ? Infinity : Number(v),
        );
        return {
          ...contract,
          totalAllocations: nullAwareSum(contractValues),
        };
      });

      // 3) Per-day group totals (null-aware)
      const dayTotalsMap: Record<string, number> = {};
      days.forEach((day) => {
        const valuesForDay = processedContracts.map(
          (contract) => contract.dayWiseAllocation?.[day] ?? Infinity,
        );
        dayTotalsMap[day] = nullAwareSum(valuesForDay);
      });

      // 4) Group total (null-aware sum of day totals)
      const totalAllocation = nullAwareSum(Object.values(dayTotalsMap));

      // 5) dayWiseTotal array — used by header and row rendering
      const dayWiseTotal = days
        .sort((a, b) => dayjs(a).unix() - dayjs(b).unix())
        .map((day) => ({
          day,
          total: dayTotalsMap[day],
        }));

      return {
        ...group,
        contracts: processedContracts,
        dayWiseTotal,
        totalAllocations: totalAllocation,
        disabled: group.disabled || totalAllocation === Infinity, // ← disable if all-null
      };
    });

    return {
      ...cp,
      groups: processedGroups,
      disabled: processedGroups.every((g) => g.disabled), // ← re-evaluate cp disable
    };
  });
};

export const generateRandomId = () => {
  const array = new Uint32Array(1);
  globalThis.crypto.getRandomValues(array);
  return array[0];
};

export const haveSameElements = (a, b) => {
  if (a.length !== b.length) {
    return false;
  }

  const counts = new Map();

  for (const num of a) {
    counts.set(num, (counts.get(num) ?? 0) + 1);
  }

  for (const num of b) {
    if (!counts.has(num)) {
      return false;
    }

    const next = counts.get(num) - 1;
    if (next === 0) {
      counts.delete(num);
    } else {
      counts.set(num, next);
    }
  }

  return counts.size === 0;
};
